import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Users, ChartLineUp, Bell, Crown, Clock, Plant, Trash, Warning, CreditCard, ArrowClockwise, MagnifyingGlass, Newspaper, Sparkle, Eye, EyeSlash, PencilSimple, DownloadSimple, EnvelopeSimple, BookOpen, Lock, LockOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmt(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [users, setUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [txOnlyUngranted, setTxOnlyUngranted] = useState(false);
  const [forceBusySid, setForceBusySid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  // ----- Blog state -----
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogTopic, setBlogTopic] = useState("");
  const [blogExtra, setBlogExtra] = useState("");
  const [blogWithImage, setBlogWithImage] = useState(true);
  const [blogPublish, setBlogPublish] = useState(true);
  const [blogBusy, setBlogBusy] = useState(false);
  const [blogBusySlug, setBlogBusySlug] = useState(null);

  // ----- Contact inbox state -----
  const [contacts, setContacts] = useState([]);
  const [contactOnlyUnread, setContactOnlyUnread] = useState(false);
  const [contactBusyId, setContactBusyId] = useState(null);

  const loadContacts = async (unread = contactOnlyUnread) => {
    try {
      const { data } = await api.get(`/admin/contacts?limit=100${unread ? "&only_unread=true" : ""}`);
      setContacts(data.items || []);
    } catch {
      toast.error("Couldn't load contacts");
    }
  };

  const markContactRead = async (id) => {
    setContactBusyId(id);
    try {
      await api.post(`/admin/contacts/${id}/read`);
      await loadContacts(contactOnlyUnread);
    } finally {
      setContactBusyId(null);
    }
  };

  const markContactHandled = async (id) => {
    setContactBusyId(id);
    try {
      await api.post(`/admin/contacts/${id}/handled`);
      await loadContacts(contactOnlyUnread);
    } finally {
      setContactBusyId(null);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    setContactBusyId(id);
    try {
      await api.delete(`/admin/contacts/${id}`);
      await loadContacts(contactOnlyUnread);
    } finally {
      setContactBusyId(null);
    }
  };

  const loadBlog = async () => {
    try {
      const { data } = await api.get("/admin/blog?limit=100");
      setBlogPosts(data.items || []);
    } catch {
      toast.error("Couldn't load blog posts");
    }
  };

  // ----- Research state -----
  const [researchPapers, setResearchPapers] = useState([]);
  const [researchTopic, setResearchTopic] = useState("");
  const [researchCategory, setResearchCategory] = useState("climate");
  const [researchExtra, setResearchExtra] = useState("");
  const [researchWithImage, setResearchWithImage] = useState(true);
  const [researchIsFree, setResearchIsFree] = useState(false);
  const [researchPublish, setResearchPublish] = useState(true);
  const [researchBusy, setResearchBusy] = useState(false);
  const [researchBusySlug, setResearchBusySlug] = useState(null);
  const RESEARCH_CATS = ["earth", "nature", "wildlife", "people", "climate"];

  const loadResearch = async () => {
    try {
      const { data } = await api.get("/admin/research?limit=100");
      setResearchPapers(data.items || []);
    } catch {
      toast.error("Couldn't load research papers");
    }
  };

  const generateResearch = async () => {
    const topic = researchTopic.trim();
    if (topic.length < 3) {
      toast.error("Topic must be at least 3 characters");
      return;
    }
    setResearchBusy(true);
    try {
      const { data } = await api.post("/admin/research/generate", {
        topic,
        category: researchCategory,
        extra_guidance: researchExtra.trim() || null,
        generate_image: researchWithImage,
        publish: researchPublish,
        is_free: researchIsFree,
      });
      if (!data.job_id) throw new Error("No job_id returned");
      toast.info("Generating paper in the background… (≈60s)");
      // Poll every 3s up to 3 minutes.
      let attempts = 0;
      const maxAttempts = 60;
      const poll = async () => {
        attempts += 1;
        try {
          const { data: j } = await api.get(`/admin/research/jobs/${data.job_id}`);
          if (j.status === "done") {
            toast.success(`Generated "${j.result_title}" (${j.word_count || "?"} words)`);
            setResearchTopic("");
            setResearchExtra("");
            await loadResearch();
            setResearchBusy(false);
            return;
          }
          if (j.status === "error") {
            toast.error(j.error || "Generation failed");
            setResearchBusy(false);
            return;
          }
        } catch (err) {
          if (attempts >= 3) {
            toast.error("Lost contact with the job. Refresh the list to check.");
            setResearchBusy(false);
            return;
          }
        }
        if (attempts >= maxAttempts) {
          toast.error("Generation is still running. Refresh the list in a minute to see it.");
          setResearchBusy(false);
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed to start");
      setResearchBusy(false);
    }
  };

  const toggleResearchPublish = async (paper) => {
    setResearchBusySlug(paper.slug);
    try {
      await api.patch(`/admin/research/${paper.slug}`, { published: !paper.published });
      await loadResearch();
    } catch { toast.error("Couldn't update paper"); }
    finally { setResearchBusySlug(null); }
  };

  const toggleResearchFree = async (paper) => {
    setResearchBusySlug(paper.slug);
    try {
      await api.patch(`/admin/research/${paper.slug}`, { is_free: !paper.is_free });
      await loadResearch();
    } catch { toast.error("Couldn't update paper"); }
    finally { setResearchBusySlug(null); }
  };

  const deleteResearch = async (paper) => {
    if (!window.confirm(`Delete "${paper.title}"? This cannot be undone.`)) return;
    setResearchBusySlug(paper.slug);
    try {
      await api.delete(`/admin/research/${paper.slug}`);
      toast.success("Paper deleted");
      await loadResearch();
    } catch { toast.error("Couldn't delete paper"); }
    finally { setResearchBusySlug(null); }
  };

  const generateBlog = async () => {
    const topic = blogTopic.trim();
    if (topic.length < 3) {
      toast.error("Topic must be at least 3 characters");
      return;
    }
    setBlogBusy(true);
    try {
      const { data } = await api.post("/admin/blog/generate", {
        topic,
        extra_guidance: blogExtra.trim() || null,
        generate_image: blogWithImage,
        publish: blogPublish,
      });
      toast.success(`Generated "${data.title}" (${data.word_count || "?"} words)`);
      setBlogTopic("");
      setBlogExtra("");
      await loadBlog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setBlogBusy(false);
    }
  };

  const togglePublish = async (post) => {
    setBlogBusySlug(post.slug);
    try {
      await api.patch(`/admin/blog/${post.slug}`, { published: !post.published });
      await loadBlog();
    } catch (e) {
      toast.error("Couldn't update post");
    } finally {
      setBlogBusySlug(null);
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setBlogBusySlug(post.slug);
    try {
      await api.delete(`/admin/blog/${post.slug}`);
      toast.success("Post deleted");
      await loadBlog();
    } catch (e) {
      toast.error("Couldn't delete post");
    } finally {
      setBlogBusySlug(null);
    }
  };

  const loadTxs = async (ungrantedOnly = txOnlyUngranted) => {
    try {
      const { data } = await api.get(`/billing/admin/transactions?limit=50${ungrantedOnly ? "&only_ungranted=true" : ""}`);
      setTxs(data.items || []);
    } catch (e) {
      toast.error("Couldn't load transactions");
    }
  };

  const refresh = async () => {
    try {
      const [{ data: o }, { data: r }, { data: u }] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/cron-runs?limit=30"),
        api.get("/admin/users?limit=200"),
      ]);
      setOverview(o);
      setRuns(r.items || []);
      setUsers(u.items || []);
      await loadTxs(txOnlyUngranted);
      await loadBlog();
      await loadResearch();
      await loadContacts(contactOnlyUnread);
    } catch {
      toast.error("Couldn't load admin data");
    }
  };

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    (async () => {
      await refresh();
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [user]);

  const runDryRun = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-accounts", { confirm: false });
      setCleanupPreview(data);
    } catch (e) {
      toast.error("Cleanup preview failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const runDelete = async () => {
    if (!cleanupPreview) return;
    const n = cleanupPreview.would_delete?.users || 0;
    if (!window.confirm(`Permanently delete ${n} user account(s) and ALL their scans, plants, notifications, payments? This cannot be undone.`)) {
      return;
    }
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-accounts", { confirm: true });
      toast.success(`Deleted ${data.deleted.users} user(s) and cascaded ${data.deleted.scans + data.deleted.plants + data.deleted.notifications + data.deleted.payment_transactions} related rows`);
      setCleanupPreview(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const forceGrant = async (tx) => {
    const reason = window.prompt(
      `Force-grant Pro for ${tx.user_email || tx.user_id}?\n\nSession: ${tx.session_id}\nAmount: ${tx.amount_total || tx.amount} ${tx.currency || ""}\n\nReason (optional, for audit log):`,
      "stripe-rescue"
    );
    if (reason === null) return; // cancelled
    setForceBusySid(tx.session_id);
    try {
      const { data } = await api.post("/billing/admin/force-grant", {
        session_id: tx.session_id,
        reason: reason || "stripe-rescue",
      });
      toast.success(`Pro granted until ${new Date(data.pro_until).toLocaleDateString()}`);
      await loadTxs(txOnlyUngranted);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force-grant failed");
    } finally {
      setForceBusySid(null);
    }
  };

  const toggleUngranted = async () => {
    const next = !txOnlyUngranted;
    setTxOnlyUngranted(next);
    await loadTxs(next);
  };

  if (user && user !== false && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-sand-50" data-testid="admin-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <div>
          <div className="font-mono-data text-mute">internal</div>
          <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">Admin dashboard</h1>
        </div>

        {loading ? (
          <div className="font-mono-data text-mute animate-pulse-soft">loading…</div>
        ) : (
          <>
            {overview && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
                <Stat label="Users" value={overview.users_total} Icon={Users} />
                <Stat label="Pro users" value={overview.pro_users} Icon={Crown} accent="text-ochre" />
                <Stat label="Scans (total)" value={overview.scans_total} Icon={ChartLineUp} />
                <Stat label="Plants" value={overview.plants_total} Icon={Plant} />
              </div>
            )}

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-cron">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-mono-data text-mute">background cron · daily alerts</div>
                  <div className="font-display text-2xl text-ink leading-tight">
                    Last run: {fmt(overview?.last_cron_run?.ran_at)}
                  </div>
                </div>
                {overview?.last_cron_run && (
                  <div className="text-right">
                    <div className="font-mono-data text-mute text-xs">users checked · alerts fired</div>
                    <div className="font-display text-2xl text-forest">
                      {overview.last_cron_run.users_checked} · <span className="text-terra">{overview.last_cron_run.alerts_created}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto rounded-xl border border-sand-200 bg-white">
                <table className="w-full text-sm" data-testid="cron-runs-table">
                  <thead className="bg-sand-50 text-mute">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">ran at</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">users checked</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">alerts created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-6 text-center text-mute">No cron runs yet.</td></tr>
                    )}
                    {runs.map((r) => (
                      <tr key={r.id} className="border-t border-sand-200">
                        <td className="px-3 py-2 font-mono-data text-xs">{fmt(r.ran_at)}</td>
                        <td className="px-3 py-2">{r.users_checked}</td>
                        <td className="px-3 py-2 text-terra font-mono">{r.alerts_created}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-transactions">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="font-mono-data text-mute mb-1 flex items-center gap-2"><CreditCard size={14} weight="duotone" /> stripe · recent sessions</div>
                  <div className="font-display text-2xl text-ink leading-tight">Payment transactions</div>
                  <p className="text-mute text-sm mt-1 max-w-xl">
                    Latest checkout sessions. Use <span className="font-mono-data text-ink">Force grant</span> to manually unlock Pro for a customer whose webhook never landed.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleUngranted}
                    data-testid="tx-toggle-ungranted"
                    className={`btn-secondary !text-xs ${txOnlyUngranted ? "!bg-ochre !text-ink !border-ochre-dark" : ""}`}
                  >
                    <MagnifyingGlass size={12} weight="duotone" /> {txOnlyUngranted ? "Showing ungranted only" : "Show only ungranted"}
                  </button>
                  <button
                    onClick={() => loadTxs(txOnlyUngranted)}
                    data-testid="tx-refresh"
                    className="btn-secondary !text-xs"
                  >
                    <ArrowClockwise size={12} weight="duotone" /> Refresh
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-sand-200 bg-white">
                <table className="w-full text-sm" data-testid="transactions-table">
                  <thead className="bg-sand-50 text-mute">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">created</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">customer</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">session_id</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">amount</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">status</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">granted</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">mode</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txs.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-6 text-center text-mute">No transactions{txOnlyUngranted ? " awaiting grant" : " yet"}.</td></tr>
                    )}
                    {txs.map((t) => {
                      const amt = t.amount_total != null ? (t.amount_total / 100).toFixed(2) : (t.amount != null ? Number(t.amount).toFixed(2) : "—");
                      const cur = (t.currency || "").toUpperCase();
                      const paid = t.payment_status === "paid";
                      return (
                        <tr key={t.id} className="border-t border-sand-200" data-testid={`tx-row-${t.session_id}`}>
                          <td className="px-3 py-2 font-mono-data text-xs">{fmt(t.created_at)}</td>
                          <td className="px-3 py-2 truncate max-w-[200px]" title={t.user_email}>{t.user_email || t.user_id || "—"}</td>
                          <td className="px-3 py-2 font-mono-data text-xs truncate max-w-[200px]" title={t.session_id}>{t.session_id}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{amt} <span className="text-mute text-xs">{cur}</span></td>
                          <td className="px-3 py-2">
                            <span className={`tag ${paid ? "severity-mild" : ""}`}>{t.payment_status || t.status || "—"}</span>
                          </td>
                          <td className="px-3 py-2">
                            {t.granted ? (
                              <span className="tag severity-moderate">granted</span>
                            ) : (
                              <span className="tag">pending</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono-data text-xs uppercase">{t.mode || "—"}</td>
                          <td className="px-3 py-2">
                            {!t.granted && (
                              <button
                                onClick={() => forceGrant(t)}
                                disabled={forceBusySid === t.session_id}
                                data-testid={`force-grant-${t.session_id}`}
                                className="btn-primary !text-xs !py-1.5 !px-3 !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white"
                              >
                                {forceBusySid === t.session_id ? "Granting…" : "Force grant"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-contacts">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="font-mono-data text-mute mb-1 flex items-center gap-2"><EnvelopeSimple size={14} weight="duotone" /> inbox · contact form</div>
                  <div className="font-display text-2xl text-ink leading-tight">Contact messages</div>
                  <p className="text-mute text-sm mt-1 max-w-xl">
                    Submissions from <span className="font-mono-data text-ink">/contact</span>. Reply directly to the sender&apos;s email, then mark as handled to clear it from the queue.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { const n = !contactOnlyUnread; setContactOnlyUnread(n); loadContacts(n); }}
                    data-testid="contact-toggle-unread"
                    className={`btn-secondary !text-xs ${contactOnlyUnread ? "!bg-ochre !text-ink !border-ochre-dark" : ""}`}
                  >
                    <MagnifyingGlass size={12} weight="duotone" /> {contactOnlyUnread ? "Showing unread only" : "Show only unread"}
                  </button>
                  <button
                    onClick={() => loadContacts(contactOnlyUnread)}
                    data-testid="contact-refresh"
                    className="btn-secondary !text-xs"
                  >
                    <ArrowClockwise size={12} weight="duotone" /> Refresh
                  </button>
                </div>
              </div>

              <div className="space-y-3" data-testid="contacts-list">
                {contacts.length === 0 && (
                  <div className="text-center text-mute p-6 bg-white border border-sand-200 rounded-xl">
                    No messages{contactOnlyUnread ? " unread" : ""} yet.
                  </div>
                )}
                {contacts.map((c) => (
                  <div
                    key={c.contact_id}
                    className={`bg-white border border-sand-200 rounded-xl p-4 ${c.read ? "opacity-70" : ""}`}
                    data-testid={`contact-row-${c.contact_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-lg text-ink">{c.name}</span>
                          <a href={`mailto:${c.email}?subject=Re: your message to A Must For Earth`} className="text-forest text-sm hover:underline break-all">
                            {c.email}
                          </a>
                          {!c.read && <span className="tag !bg-ochre/20 !text-ochre-dark !border-ochre/40">new</span>}
                          {c.handled && <span className="tag severity-mild">handled</span>}
                        </div>
                        <div className="font-mono-data text-mute text-xs mt-1">{fmt(c.created_at)}</div>
                        <p className="mt-3 text-ink/90 whitespace-pre-wrap leading-relaxed">{c.message}</p>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[110px]">
                        {!c.read && (
                          <button
                            onClick={() => markContactRead(c.contact_id)}
                            disabled={contactBusyId === c.contact_id}
                            data-testid={`contact-mark-read-${c.contact_id}`}
                            className="btn-secondary !text-xs !py-1.5"
                          >
                            <Eye size={12} weight="duotone" /> Mark read
                          </button>
                        )}
                        {!c.handled && (
                          <button
                            onClick={() => markContactHandled(c.contact_id)}
                            disabled={contactBusyId === c.contact_id}
                            data-testid={`contact-mark-handled-${c.contact_id}`}
                            className="btn-primary !text-xs !py-1.5 !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white"
                          >
                            <CreditCard size={12} weight="duotone" /> Mark handled
                          </button>
                        )}
                        <button
                          onClick={() => deleteContact(c.contact_id)}
                          disabled={contactBusyId === c.contact_id}
                          data-testid={`contact-delete-${c.contact_id}`}
                          className="text-terra hover:text-terra-dark text-xs inline-flex items-center gap-1 justify-center"
                        >
                          <Trash size={12} /> delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-research">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="font-mono-data text-mute mb-1 flex items-center gap-2"><BookOpen size={14} weight="duotone" /> content · research imprint</div>
                  <div className="font-display text-2xl text-ink leading-tight">Generate a research paper</div>
                  <p className="text-mute text-sm mt-1 max-w-xl">
                    GPT-5.5 writes a 1000–2000 word structured brief with citations. Papers publish to <span className="font-mono-data text-ink">/research</span>.
                    Paywalled by default (Grower only). Tick <span className="font-mono-data text-ink">Free for everyone</span> to unlock a paper for public reading.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 bg-white border border-sand-200 rounded-xl p-4">
                <div className="md:col-span-2">
                  <label className="font-mono-data text-mute text-xs">Topic</label>
                  <input
                    value={researchTopic}
                    onChange={(e) => setResearchTopic(e.target.value)}
                    data-testid="research-topic"
                    placeholder="e.g. Coral reef bleaching thresholds under 1.5°C warming"
                    className="mt-1 w-full bg-sand-50 border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <div>
                  <label className="font-mono-data text-mute text-xs">Focus area</label>
                  <select
                    value={researchCategory}
                    onChange={(e) => setResearchCategory(e.target.value)}
                    data-testid="research-category"
                    className="mt-1 w-full bg-sand-50 border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest capitalize"
                  >
                    {RESEARCH_CATS.map((c) => (
                      <option key={c} value={c} className="capitalize">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-mono-data text-mute text-xs">Extra guidance (optional)</label>
                  <input
                    value={researchExtra}
                    onChange={(e) => setResearchExtra(e.target.value)}
                    data-testid="research-extra"
                    placeholder="Specific region, angle, or timeframe"
                    className="mt-1 w-full bg-sand-50 border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input type="checkbox" checked={researchWithImage} onChange={(e) => setResearchWithImage(e.target.checked)} data-testid="research-with-image" />
                  Generate hero image (Nano Banana)
                </label>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input type="checkbox" checked={researchPublish} onChange={(e) => setResearchPublish(e.target.checked)} data-testid="research-publish" />
                  Publish immediately
                </label>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer md:col-span-2 bg-ochre/10 border border-ochre/30 rounded-lg px-3 py-2">
                  <input type="checkbox" checked={researchIsFree} onChange={(e) => setResearchIsFree(e.target.checked)} data-testid="research-is-free" />
                  <LockOpen size={14} weight="duotone" className="text-ochre-dark" />
                  Free for everyone (unlocks paper for non-subscribers)
                </label>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    onClick={generateResearch}
                    disabled={researchBusy}
                    data-testid="research-generate-btn"
                    className="btn-primary !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white"
                  >
                    <Sparkle size={14} weight="fill" /> {researchBusy ? "Generating… (background)" : "Generate paper"}
                  </button>
                </div>
              </div>

              <div className="mt-5 overflow-x-auto rounded-xl border border-sand-200 bg-white">
                <table className="w-full text-sm" data-testid="research-table">
                  <thead className="bg-sand-50 text-mute">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">created</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">title</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">category</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">words</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">access</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">status</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {researchPapers.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-mute">No research papers yet. Generate your first one above.</td></tr>
                    )}
                    {researchPapers.map((p) => (
                      <tr key={p.slug} className="border-t border-sand-200" data-testid={`research-row-${p.slug}`}>
                        <td className="px-3 py-2 font-mono-data text-xs">{fmt(p.created_at)}</td>
                        <td className="px-3 py-2 truncate max-w-[240px]">
                          <Link to={`/research/${p.slug}`} target="_blank" className="text-forest hover:underline">{p.title}</Link>
                          <div className="text-xs text-mute font-mono-data truncate">/{p.slug}</div>
                        </td>
                        <td className="px-3 py-2 capitalize text-xs">{p.category || "—"}</td>
                        <td className="px-3 py-2 font-mono-data text-xs">{p.word_count || "—"}</td>
                        <td className="px-3 py-2">
                          {p.is_free ? (
                            <span className="tag !bg-forest !text-white !border-forest inline-flex items-center gap-1"><LockOpen size={10} weight="fill" /> free</span>
                          ) : (
                            <span className="tag !bg-ochre/20 !text-ochre-dark !border-ochre/40 inline-flex items-center gap-1"><Lock size={10} weight="fill" /> Grower</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`tag ${p.published ? "severity-mild" : ""}`}>{p.published ? "published" : "draft"}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => toggleResearchFree(p)}
                              disabled={researchBusySlug === p.slug}
                              data-testid={`research-toggle-free-${p.slug}`}
                              className="btn-secondary !text-xs !py-1 !px-2"
                              title={p.is_free ? "Move to Grower-only" : "Publish as Free"}
                            >
                              {p.is_free ? <Lock size={12} weight="duotone" /> : <LockOpen size={12} weight="duotone" />}
                              {p.is_free ? "Make paid" : "Make free"}
                            </button>
                            <button
                              onClick={() => toggleResearchPublish(p)}
                              disabled={researchBusySlug === p.slug}
                              data-testid={`research-toggle-pub-${p.slug}`}
                              className="btn-secondary !text-xs !py-1 !px-2"
                            >
                              {p.published ? <EyeSlash size={12} weight="duotone" /> : <Eye size={12} weight="duotone" />}
                              {p.published ? "Unpublish" : "Publish"}
                            </button>
                            <button
                              onClick={() => deleteResearch(p)}
                              disabled={researchBusySlug === p.slug}
                              data-testid={`research-delete-${p.slug}`}
                              className="text-terra hover:text-terra-dark text-xs inline-flex items-center gap-1"
                            >
                              <Trash size={12} /> delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-blog">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="font-mono-data text-mute mb-1 flex items-center gap-2"><Newspaper size={14} weight="duotone" /> content · blog</div>
                  <div className="font-display text-2xl text-ink leading-tight">Generate a blog post</div>
                  <p className="text-mute text-sm mt-1 max-w-xl">
                    Type a topic (e.g. <span className="font-mono-data text-ink">&quot;Heat stress in tomato plants&quot;</span>).
                    GPT-5.5 writes a 400–500 word post and Nano Banana paints a hero image. Saved to <span className="font-mono-data text-ink">/blog</span>.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 bg-white border border-sand-200 rounded-xl p-4">
                <div className="md:col-span-2">
                  <label className="font-mono-data text-mute text-xs">Topic</label>
                  <input
                    value={blogTopic}
                    onChange={(e) => setBlogTopic(e.target.value)}
                    data-testid="blog-topic"
                    placeholder="e.g. How to spot early heat stress in tomato plants"
                    className="mt-1 w-full bg-sand-50 border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono-data text-mute text-xs">Extra guidance (optional)</label>
                  <textarea
                    value={blogExtra}
                    onChange={(e) => setBlogExtra(e.target.value)}
                    data-testid="blog-extra"
                    rows={2}
                    placeholder="Tone, angle, specific climate region, etc."
                    className="mt-1 w-full bg-sand-50 border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input type="checkbox" checked={blogWithImage} onChange={(e) => setBlogWithImage(e.target.checked)} data-testid="blog-with-image" />
                  Generate hero image (Nano Banana)
                </label>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input type="checkbox" checked={blogPublish} onChange={(e) => setBlogPublish(e.target.checked)} data-testid="blog-publish" />
                  Publish immediately
                </label>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    onClick={generateBlog}
                    disabled={blogBusy}
                    data-testid="blog-generate-btn"
                    className="btn-primary !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white"
                  >
                    <Sparkle size={14} weight="fill" /> {blogBusy ? "Generating… (≈30s)" : "Generate post"}
                  </button>
                </div>
              </div>

              <div className="mt-5 overflow-x-auto rounded-xl border border-sand-200 bg-white">
                <table className="w-full text-sm" data-testid="blog-posts-table">
                  <thead className="bg-sand-50 text-mute">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">created</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">title</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">tags</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">image</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">status</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogPosts.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-mute">No posts yet. Generate your first one above.</td></tr>
                    )}
                    {blogPosts.map((p) => (
                      <tr key={p.slug} className="border-t border-sand-200" data-testid={`blog-row-${p.slug}`}>
                        <td className="px-3 py-2 font-mono-data text-xs">{fmt(p.created_at)}</td>
                        <td className="px-3 py-2 truncate max-w-[260px]">
                          <Link to={`/blog/${p.slug}`} target="_blank" className="text-forest hover:underline">{p.title}</Link>
                          <div className="text-xs text-mute font-mono-data truncate">/{p.slug}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(p.tags || []).slice(0, 3).map((t, i) => (
                              <span key={i} className="tag severity-mild">{t}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">{p.image_b64 ? "yes" : <span className="text-mute">no</span>}</td>
                        <td className="px-3 py-2">
                          <span className={`tag ${p.published ? "severity-mild" : ""}`}>{p.published ? "published" : "draft"}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => togglePublish(p)}
                              disabled={blogBusySlug === p.slug}
                              data-testid={`blog-toggle-${p.slug}`}
                              className="btn-secondary !text-xs !py-1 !px-2"
                              title={p.published ? "Unpublish" : "Publish"}
                            >
                              {p.published ? <EyeSlash size={12} weight="duotone" /> : <Eye size={12} weight="duotone" />}
                              {p.published ? "Unpublish" : "Publish"}
                            </button>
                            <button
                              onClick={() => deletePost(p)}
                              disabled={blogBusySlug === p.slug}
                              data-testid={`blog-delete-${p.slug}`}
                              className="text-terra hover:text-terra-dark text-xs inline-flex items-center gap-1"
                            >
                              <Trash size={12} /> delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-brand">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="font-mono-data text-mute mb-1 flex items-center gap-2"><Sparkle size={14} weight="duotone" /> brand · assets</div>
                  <div className="font-display text-2xl text-ink leading-tight">Logo downloads</div>
                  <p className="text-mute text-sm mt-1 max-w-xl">
                    Hi-res copies of the A Must For Earth leaf logo, ready for press kits, social, partnerships, and printing.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white border border-sand-200 rounded-xl p-4 flex items-center gap-4">
                  <img src="/logo-2000.png" alt="Leaf logo 2000px" className="w-20 h-20 rounded-xl bg-sand-50 object-contain" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-ink">PNG · 2000 × 2000</div>
                    <div className="text-xs text-mute font-mono-data">leaf logo · transparent</div>
                    <a
                      href="/logo-2000.png"
                      download="amustforearth-logo-2000.png"
                      data-testid="logo-download-png"
                      className="btn-primary !text-xs !py-1.5 !px-3 mt-3 inline-flex"
                    >
                      <DownloadSimple size={12} weight="bold" /> Download PNG
                    </a>
                  </div>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-4 flex items-center gap-4">
                  <img src="/favicon.svg" alt="Leaf logo SVG" className="w-20 h-20 rounded-xl bg-sand-50 p-3 object-contain" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-ink">SVG · vector</div>
                    <div className="text-xs text-mute font-mono-data">infinitely scalable</div>
                    <a
                      href="/favicon.svg"
                      download="amustforearth-logo.svg"
                      data-testid="logo-download-svg"
                      className="btn-secondary !text-xs !py-1.5 !px-3 mt-3 inline-flex"
                    >
                      <DownloadSimple size={12} weight="bold" /> Download SVG
                    </a>
                  </div>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-4 flex items-center gap-4 sm:col-span-2 lg:col-span-1">
                  <img src="/og-card-1200x630.png" alt="Open Graph social card" className="w-32 h-[68px] rounded-xl bg-sand-50 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-ink">OG card · 1200 × 630</div>
                    <div className="text-xs text-mute font-mono-data">social preview · X / FB / LinkedIn</div>
                    <a
                      href="/og-card-1200x630.png"
                      download="amustforearth-og-card.png"
                      data-testid="og-download-png"
                      className="btn-secondary !text-xs !py-1.5 !px-3 mt-3 inline-flex"
                    >
                      <DownloadSimple size={12} weight="bold" /> Download OG card
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-cleanup">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono-data text-mute mb-2">danger zone · cleanup</div>
                  <div className="font-display text-2xl text-ink leading-tight">Purge test accounts</div>
                  <p className="text-mute text-sm mt-1 max-w-xl">
                    Deletes every user except <span className="font-mono-data text-ink">admin@amustforearth.com</span> and{" "}
                    <span className="font-mono-data text-ink">mail@aigraphicstock.com</span>, plus all their scans, plants,
                    notifications and payment transactions. The currently-signed-in admin is always preserved.
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {!cleanupPreview && (
                    <button onClick={runDryRun} disabled={cleanupBusy} data-testid="cleanup-dry-run" className="btn-secondary !text-sm">
                      <Warning size={14} weight="duotone" /> {cleanupBusy ? "Checking…" : "Preview cleanup"}
                    </button>
                  )}
                </div>
              </div>

              {cleanupPreview && (
                <div className="mt-5 bg-white border border-sand-200 rounded-xl p-4" data-testid="cleanup-preview">
                  <div className="font-mono-data text-mute mb-3">would delete</div>
                  <div className="grid sm:grid-cols-5 gap-3 mb-4">
                    <Mini label="Users" value={cleanupPreview.would_delete.users} />
                    <Mini label="Scans" value={cleanupPreview.would_delete.scans} />
                    <Mini label="Plants" value={cleanupPreview.would_delete.plants} />
                    <Mini label="Notifications" value={cleanupPreview.would_delete.notifications} />
                    <Mini label="Payments" value={cleanupPreview.would_delete.payment_transactions} />
                  </div>
                  <div className="font-mono-data text-mute text-xs mb-2">
                    keeping: {(cleanupPreview.keep_emails || []).join(", ")}
                  </div>
                  {cleanupPreview.target_emails?.length > 0 && (
                    <details className="text-xs text-mute mt-1">
                      <summary className="cursor-pointer hover:text-ink">
                        Show {cleanupPreview.target_emails.length} target email(s){cleanupPreview.target_emails_truncated ? " (truncated to first 200)" : ""}
                      </summary>
                      <div className="mt-2 max-h-48 overflow-y-auto font-mono-data leading-relaxed">
                        {cleanupPreview.target_emails.map((e, i) => <div key={i}>{e}</div>)}
                      </div>
                    </details>
                  )}
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={runDelete}
                      disabled={cleanupBusy || cleanupPreview.would_delete.users === 0}
                      data-testid="cleanup-confirm-delete"
                      className="btn-primary !bg-terra hover:!bg-terra-dark !text-sm"
                    >
                      <Trash size={14} weight="bold" /> {cleanupBusy ? "Deleting…" : `Permanently delete ${cleanupPreview.would_delete.users} account(s)`}
                    </button>
                    <button
                      onClick={() => setCleanupPreview(null)}
                      data-testid="cleanup-cancel"
                      className="btn-secondary !text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="admin-users">
              <div className="font-mono-data text-mute mb-3">users · most recent {users.length}</div>
              <div className="overflow-x-auto rounded-xl border border-sand-200 bg-white">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead className="bg-sand-50 text-mute">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">email</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">plan</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">lang</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">scans</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">plants</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">unread</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">location</th>
                      <th className="text-left px-3 py-2 font-mono-data text-xs">last check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-sand-200">
                        <td className="px-3 py-2 truncate max-w-[220px]">{u.email}</td>
                        <td className="px-3 py-2">
                          <span className={`tag ${u.subscription_status === "pro" ? "severity-moderate" : ""}`}>
                            {u.subscription_status}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono-data text-xs uppercase">{u.language}</td>
                        <td className="px-3 py-2">{u.scans}</td>
                        <td className="px-3 py-2">{u.plants}</td>
                        <td className="px-3 py-2 text-terra">{u.unread_notifications}</td>
                        <td className="px-3 py-2 truncate max-w-[180px]">{u.last_known_label || "—"}</td>
                        <td className="px-3 py-2 font-mono-data text-xs">{fmt(u.last_alert_check_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, Icon, accent }) {
  return (
    <div className="bg-sand-100 border border-sand-200 rounded-2xl p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-2xl bg-forest text-white flex items-center justify-center ${accent ? "!bg-ochre !text-ink" : ""}`}>
        <Icon size={22} weight="duotone" />
      </div>
      <div>
        <div className="font-mono-data text-mute">{label}</div>
        <div className={`font-display text-3xl leading-none ${accent || "text-ink"}`}>{value}</div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-sand-50 border border-sand-200 rounded-xl p-3 text-center">
      <div className="font-mono-data text-mute text-xs">{label}</div>
      <div className="font-display text-2xl text-ink leading-none mt-1">{value}</div>
    </div>
  );
}
